import React, { useEffect, useRef, useState } from "react";
import styles from './ReadingDayLog.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/button/Button";
import { Accordion, AccordionItem } from "../../../components/accordion/Accordion";
import ReadingDayFrom from "./readingDayForm/ReadingDayForm";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import Modal from "../../../components/modal/Modal";

const ReadingDayLog = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [studentYearList, setStudentYearList] = useState([]);
  const [isValidated, setIsValidated] = useState(false);
  const [readingDayFormData, setReadingDayFormData] = useState({
    dateOfCase: '',
    yearId: null,
    startTime: '',
    endTime: '',
  })
  const [readingDayFormErrorsCount, setReadingDayFormErrorsCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [student, setStudent] = useState({});
  const readingDayFormRef = useRef();
  const { logReportId, studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`case-log/options`, {
        params: studentId ? {
          studentId
        } : {}
      })
      .then((response) => {
        if(response.data?.studentYear) {
          setStudentYearList([{
            value: response.data.studentYear.id,
            label: response.data.studentYear.year,
          }]);
        }
      })
    }

    fetchData();
  }, [studentId]);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`non-clinical-case-log/${logReportId}`)
      .then((response) => {
        if(response.data?.student){
          setStudent(response.data.student)
        }
        setReadingDayFormData((prevData) => ({
          ...prevData,
          dateOfCase: response.data?.dateOfCase,
          yearId: response.data?.studentYear?.id,
          startTime: response.data?.startTime,
          endTime: response.data?.endTime,
        }));
        setStatus(response.data?.status)
      })
      .catch((error) => {
        if(error.status === 404) {
          navigate('/404');
        } else {
          showToast(error.message, 'error');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    }

    fetchData();
  }, [logReportId, showToast, navigate]);

  const validateReadingDayForm = () => {
    const errorsCount = readingDayFormRef.current.validateForm();
    if(errorsCount) {
      setReadingDayFormErrorsCount(errorsCount);
      return false;
    } else {
      setReadingDayFormErrorsCount(0);
      return true;
    }
  }

  const handleValidate = () => {
    setIsValidated(true);
    const isReadingDayValid = validateReadingDayForm();
    if (isReadingDayValid) {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if(isValidated) {
      validateReadingDayForm();
    }
  }, [readingDayFormData, isValidated]);

  const handleSave = async (data) => {
    await axiosClient.patch(`non-clinical-case-log/${logReportId}`, data)
    .catch((error) => {
      showToast(error.message, 'error');
    });
  };

  const handleSubmit = async () => {
    setShowModal(false);
    setIsLoading(true);
    await axiosClient.patch(`log-reports/${logReportId}/submit`)
    .then((response) => {
      showToast(response.message);
      setIsValidated(false);
      navigate(`/log-reports`);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={studentId ? [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, path: `/log-reports/student/${studentId}` },
            { label: `Reading Day - ${logReportId}` }
          ] : [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `Reading Day - ${logReportId}` }
          ]}
        />
        {status !== 'Completed' && !isLoading && <Button
          label="Submit"
          size="medium"
          onClick={handleValidate}
        />}
      </div>
      <div className={styles.container}>
        <Accordion key={logReportId}>
          <AccordionItem
            title={
              <span className={styles.accordioTitle}>
                Reading Day Information
                {readingDayFormErrorsCount > 0 && <span className={styles.count}>{readingDayFormErrorsCount}</span>}
              </span>
            }
          >
            <ReadingDayFrom
              formData={readingDayFormData}
              studentYearList={studentYearList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setReadingDayFormData((prevData) => ({
                  ...prevData,
                  ...updatedData
                }))
              }}
              ref={readingDayFormRef}
              disabled={status === 'Completed'}
            />
          </AccordionItem>
        </Accordion>
      </div>
      {showModal && <Modal>
        <div className={styles.submitModal}>
          <h2 className={styles.modalTitle}>Are you sure you want to submit reading day report?</h2>
          <div className={styles.buttons}>
            <Button
              label="Cancel"
              size="small"
              variant="white"
              onClick={() => setShowModal(false)}
            />
            <Button
              label="Submit"
              size="small"
              onClick={() => handleSubmit()}
            />
          </div>
        </div>
      </Modal>}
    </>
  );
};

export default ReadingDayLog;